import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';

const MESSAGE_SELECT = {
  id: true, channelId: true, conversationId: true, senderId: true, parentId: true,
  content: true, contentType: true, metadata: true, isEdited: true, isDeleted: true,
  createdAt: true, updatedAt: true,
  sender: { select: { id: true, displayName: true, avatarUrl: true } },
  reactions: { include: { user: { select: { id: true, displayName: true } } } },
  attachments: { select: { id: true, filename: true, mimeType: true, fileSize: true, storageUrl: true } },
  parent: {
    select: {
      id: true,
      content: true,
      contentType: true,
      isDeleted: true,
      sender: { select: { id: true, displayName: true } },
      attachments: { select: { id: true, filename: true, mimeType: true } }
    }
  },
};

export async function getChannelMessages(channelId: string, cursor?: string, limit = 50) {
  const messages = await prisma.message.findMany({
    where: { channelId },
    select: MESSAGE_SELECT as any,
    orderBy: { createdAt: 'desc' },
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
  });
  return messages.reverse();
}

export async function getConversationMessages(conversationId: string, cursor?: string, limit = 50) {
  const messages = await prisma.message.findMany({
    where: { conversationId },
    select: MESSAGE_SELECT as any,
    orderBy: { createdAt: 'desc' },
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
  });
  return messages.reverse();
}

export async function createMessage(data: {
  channelId?: string;
  conversationId?: string;
  senderId: string;
  content: string;
  contentType?: string;
  parentId?: string;
  metadata?: any;
}) {
  return prisma.message.create({
    data: { ...data, contentType: (data.contentType as any) ?? 'text' },
    select: MESSAGE_SELECT as any,
  });
}

export async function editMessage(messageId: string, userId: string, content: string) {
  const msg = await prisma.message.findUnique({ where: { id: messageId } });
  if (!msg) throw new AppError(404, 'Message not found');
  if (msg.senderId !== userId) throw new AppError(403, 'Cannot edit another user\'s message');
  return prisma.message.update({
    where: { id: messageId },
    data: { content, isEdited: true },
    select: MESSAGE_SELECT as any,
  });
}

export async function deleteMessage(messageId: string, userId: string, isAdmin = false) {
  const msg = await prisma.message.findUnique({ where: { id: messageId } });
  if (!msg) throw new AppError(404, 'Message not found');
  if (!isAdmin && msg.senderId !== userId) throw new AppError(403, 'Forbidden');
  return prisma.message.update({ where: { id: messageId }, data: { isDeleted: true, content: '[deleted]' } });
}

export async function addReaction(messageId: string, userId: string, emoji: string) {
  return prisma.reaction.upsert({
    where: { messageId_userId_emoji: { messageId, userId, emoji } },
    update: {},
    create: { messageId, userId, emoji },
  });
}

export async function removeReaction(messageId: string, userId: string, emoji: string) {
  return prisma.reaction.deleteMany({ where: { messageId, userId, emoji } });
}

export async function getThread(parentId: string) {
  return prisma.message.findMany({
    where: { parentId },
    select: MESSAGE_SELECT as any,
    orderBy: { createdAt: 'asc' },
  });
}

export async function pinMessage(messageId: string, userId: string) {
  const msg = await prisma.message.findUnique({ where: { id: messageId } });
  if (!msg) throw new AppError(404, 'Message not found');
  const meta: any = typeof msg.metadata === 'object' && msg.metadata !== null ? msg.metadata : {};
  meta.isPinned = true;
  meta.pinnedBy = userId;
  meta.pinnedAt = new Date().toISOString();
  return prisma.message.update({
    where: { id: messageId },
    data: { metadata: meta },
    select: MESSAGE_SELECT as any,
  });
}

export async function unpinMessage(messageId: string) {
  const msg = await prisma.message.findUnique({ where: { id: messageId } });
  if (!msg) throw new AppError(404, 'Message not found');
  const meta: any = typeof msg.metadata === 'object' && msg.metadata !== null ? msg.metadata : {};
  meta.isPinned = false;
  return prisma.message.update({
    where: { id: messageId },
    data: { metadata: meta },
    select: MESSAGE_SELECT as any,
  });
}

export async function getPinnedChannelMessages(channelId: string) {
  return prisma.message.findMany({
    where: { 
      channelId,
      metadata: { path: ['isPinned'], equals: true }
    },
    select: MESSAGE_SELECT as any,
    orderBy: { createdAt: 'desc' }
  });
}

export async function getPinnedConversationMessages(conversationId: string) {
  return prisma.message.findMany({
    where: { 
      conversationId,
      metadata: { path: ['isPinned'], equals: true }
    },
    select: MESSAGE_SELECT as any,
    orderBy: { createdAt: 'desc' }
  });
}
